/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { ErrorMessage } from "formik";
import { aboutClinicSchema, iFee, iClinicProfile, iLocation } from "ps-shared";
import * as yup from "yup";
import FormikSwitch from "src/components/Forms/FormikSwitch";
import FormikMultiSelect, { SelectOption } from "src/components/Forms/FormikMultiSelect";
import { FormikMultiCreateSelect } from "src/components/Forms/FormikMultiCreateSelect";
import FormikDatetime from "src/components/Forms/FormikDatetime";
import paymentList from "ps-shared/models/forms/payment.json";
import specialtyList from "ps-shared/models/forms/specialties.json";
import serviceList from "ps-shared/models/forms/services.json";
import populationList from "ps-shared/models/forms/providerPopulation.json";
import insuranceList from "ps-shared/models/forms/insurance.json";
import substancesList from "ps-shared/models/forms/substances.json";
import FormikMultiAddress, { ValidationFuncResult } from "src/components/Forms/FormikMultiAddress";
import { FormikMoneyRangeSlider } from "src/components/Forms/FormikMoneyRangeSlider";
import { RangeSliderValue } from "src/components/Forms/RangeSlider";
import commonCurrencies from "ps-shared/models/forms/commonCurrencies.json";
import Switch from "react-bootstrap-switch";
import { FormGroup, Label, UncontrolledTooltip } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons/faInfoCircle";

type AboutClinicSchema = yup.InferType<typeof aboutClinicSchema>;
interface AboutClinicProps {
  values: AboutClinicSchema;
  profile: iClinicProfile;
}

interface AboutClinicState {
  paymentOptions: SelectOption[];
  specialties: SelectOption[];
  services: SelectOption[];
  population: SelectOption[];
  insurance: SelectOption[];
  substances: SelectOption[];
  rangeDefault: RangeSliderValue;
  shareFees: boolean;
  commonCurrencies: SelectOption[];
}
export default class AboutClinic extends React.Component<AboutClinicProps, AboutClinicState> {
  private autocomplete: any;

  private autocompleteListener: any;

  constructor(props: AboutClinicProps) {
    super(props);
    this.state = {
      insurance: insuranceList.map((insurance: any) => {
        const opt = {} as SelectOption;
        opt.label = insurance;
        opt.value = insurance;
        return opt;
      }),
      paymentOptions: paymentList.map((payment: any) => {
        const opt = {} as SelectOption;
        opt.label = payment;
        opt.value = payment;
        return opt;
      }),
      population: populationList.map((pop: any) => {
        const opt = {} as SelectOption;
        opt.label = pop;
        opt.value = pop;
        return opt;
      }),
      services: serviceList.map((service: any) => {
        const opt = {} as SelectOption;
        opt.label = service;
        opt.value = service;
        return opt;
      }),
      substances: substancesList.map((substance: any) => {
        const opt = {} as SelectOption;
        opt.label = substance;
        opt.value = substance;
        return opt;
      }),
      specialties: specialtyList.map((specialty: any) => {
        const opt = {} as SelectOption;
        opt.label = specialty;
        opt.value = specialty;
        return opt;
      }),
      commonCurrencies: commonCurrencies.map((currency: string) => {
        return {
          value: currency,
          label: currency,
        } as SelectOption;
      }),
      rangeDefault: {
        min: 50,
        max: 150,
      },
      shareFees: !!this.props.values.fee,
    };
    this.autocomplete = null;
    this.handleFeeSwitch = this.handleFeeSwitch.bind(this);
  }

  public handleFeeSwitch(state: boolean) {
    if (!state) {
      this.props.values.fee = null;
    } else {
      this.props.values.fee = {
        min: this.state.rangeDefault.min,
        max: this.state.rangeDefault.max,
        currency: this.state.commonCurrencies[0].value,
      } as iFee;
    }
    this.setState({
      shareFees: state,
    });
  }

  public componentWillUnmount() {
    if (this.autocompleteListener) {
      this.autocompleteListener.remove();
    }
  }

  // tslint:disable:max-line-length
  public render() {
    return (
      <>
        <FormGroup className="mb-3 date" id="datetimepicker">
          <Label for="practiceYears">When was your clinic founded?</Label>
          <br />
          <FormikDatetime
            placeholder="Clinic founded"
            timeFormat={false}
            name="founded"
            defaultValue={this.props.values.founded}
          />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="locations">Clinic Address:</Label>
          <br />
          <FormikMultiAddress
            max={1}
            values={this.props.values.locations as unknown as iLocation[]}
            name="locations"
            placeHolder="Start typing address (full address required)"
            validationFunc={(val) => {
              if (val.city && val.region && val.country && val.postalCode && val.address) {
                return {
                  valid: true,
                } as ValidationFuncResult;
              }
              return {
                valid: false,
                err: "A full address is required",
              } as ValidationFuncResult;
            }}
          />
          <ErrorMessage name="locations" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="online">Do you offer online Sessions?</Label>&nbsp;&nbsp;
          <FormikSwitch name="online" onText="Yes" offText="No" />
          <br />
          <ErrorMessage name="online" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="inPerson">Do you offer In-Person Sessions?</Label>&nbsp;&nbsp;
          <FormikSwitch name="inPerson" onText="Yes" offText="No" />
          <br />
          <ErrorMessage name="inPerson" component="div" className="form-feedback" />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="acceptingTT">
            If 'no' is selected, the contact form (emails) on your profile will be disabled.
          </UncontrolledTooltip>
          <Label for="acceptingClients">Are you currently accepting clients?</Label>&nbsp;
          <span id="acceptingTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          &nbsp;&nbsp;
          <FormikSwitch name="acceptingClients" onText="Yes" offText="No" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="slidingScale">Is your fee sliding scale?</Label>&nbsp;&nbsp;
          <FormikSwitch name="slidingScale" onText="Yes" offText="No" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="slidingScale">Do you want to publish your fee range?</Label>&nbsp;&nbsp;
          <Switch
            defaultValue={this.state.shareFees}
            offColor="primary"
            offText="No"
            onColor="primary"
            onText="Yes"
            onChange={(state) => {
              this.handleFeeSwitch(state.state.value);
            }}
          />
        </FormGroup>
        {this.state.shareFees && (
          <FormGroup className="mb-3">
            <Label for="fee">Fee Range:</Label>
            <br />
            <FormikMoneyRangeSlider
              currencyValue={(this.props.values as any).fee ? this.props.values.fee.currency : null}
              currencyOptions={this.state.commonCurrencies}
              value={this.props.values.fee ? this.props.values.fee : this.state.rangeDefault}
              name="fee"
              min={0}
              max={250}
            />
          </FormGroup>
        )}
        <FormGroup className="mb-3">
          <Label for="payment">Payment Options:</Label>
          <br />
          <FormikMultiSelect options={this.state.paymentOptions} value={this.props.values.payment} name="payment" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="insurance">Insurance:</Label>
          <br />
          <FormikMultiCreateSelect
            options={this.state.insurance}
            value={this.props.values.insurance}
            name="insurance"
          />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="specialties">Specialties:</Label>
          <br />
          <FormikMultiSelect
            options={this.state.specialties}
            value={this.props.values.specialties}
            name="specialties"
          />
        </FormGroup>
        <FormGroup className="mb-3">
          <UncontrolledTooltip delay={0} placement="right" target="servicesTT">
            Services refer to services your practice offers. Choose from the list or type new items.
          </UncontrolledTooltip>
          <Label for="services">Services Offered:</Label>&nbsp;
          <span id="servicesTT" style={{ fontSize: "10px" }}>
            <FontAwesomeIcon icon={faInfoCircle} />
          </span>
          <br />
          <FormikMultiCreateSelect options={this.state.services} value={this.props.values.services} name="services" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="substance">Substances:</Label>
          <br />
          <FormikMultiSelect options={this.state.substances} value={this.props.values.substance} name="substance" />
        </FormGroup>
        <FormGroup className="mb-3">
          <Label for="population">Patient Population:</Label>
          <br />
          <FormikMultiSelect options={this.state.population} value={this.props.values.population} name="population" />
        </FormGroup>
      </>
    );
  }
}
